/**
 * Resolve after the given duration, or reject with an abort error as soon as the signal aborts.
 *
 * @param durationMs - how long to wait
 * @param signal - aborts the wait
 * @returns a promise that settles accordingly
 */
export function wait(durationMs: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(newAbortError());
            return;
        }

        const timer = setTimeout(() => {
            signal?.removeEventListener('abort', abort);
            resolve();
        }, durationMs);

        /** Settle the wait as aborted. */
        function abort() {
            clearTimeout(timer);
            reject(newAbortError());
        }

        signal?.addEventListener('abort', abort, { once: true });
    });
}

/**
 * Build the error an aborted wait rejects with, the same one a fetch rejects with on abort.
 *
 * @returns the abort error
 */
function newAbortError(): DOMException {
    return new DOMException('The wait was aborted', 'AbortError');
}
